import imMap, {
  imOpIsMap,
  imOpEquals,
  imOpMerge
} from '@bumblehead/im'

import {
  gnedgeIdGet as edgeIdGet,
  gnedgeIdCreatePart as edgeIdCreatePart,
  gnedgeMetaSenderKeyGet as edgeMetaSenderKeyGet,
  gnedgeMetaTargetKeyGet as edgeMetaTargetKeyGet,
  gnedgeIsSame as edgeIsSame
} from './gnedge.js'

import {
  gnpathJoin as pathJoin
} from './gnpath.js'

import {
  gnaccNSMap as accNSMap
} from './gnacc.js'

import {
  gnarrElemMv as arrElemMv
} from './gnarr.js'

import {
  gnenumKEY as KEY,
  gnenumCHILDKEYS as CHILDKEYS,
  gnenumCHILDKEYNAME as CHILDKEYNAME,
  gnenumCHILDKEYSDOM as CHILDKEYSDOM,
  gnenumNSSENDERS as NSSENDERS,
  gnenumNSTARGETS as NSTARGETS
} from './gnenum.js'

const emptylist = []
const spacesre = /\s/g

// 'name' is required and must be unique among sibling nodes
// else unexepected behaviour may occur
const gnnodeNameIsValid = name =>
  Boolean(typeof name === 'string' && name.length)

const gnnodeKeyIsValid = key =>
  Boolean(typeof key === 'string' && key.startsWith('/'))

const gnnodeKeyFormat = str => str.replace(spacesre, '-')

// graph-like nodes with childarr _references_ to other node definitions
const gnnodeCreate = (spec, name, key) => (
  imOpMerge(imMap(spec), {
    name,
    key: gnnodeKeyFormat(key || name)
    //
    // other elements defined when populated,
    //
    // childarr: []
    // datainarr: [],
    // dataoutarr: []
  }))

// CHILDKEYS lists all child keys of node
// CHILDKEYSDOM lists ui/dom-rendering child keys of node
const gnnodeChildKeysDOMGet = node => (
  node.get(CHILDKEYSDOM, emptylist))

const gnnodeChildKeysDOMSet = (node, list) => (
  node.set(CHILDKEYSDOM, list))

// returns node w/childkey-removed and index position of childkey
const gnnodeChildKeysDOMRm = (node, ckey, childindex = null) => {
  if (node.has(CHILDKEYNAME) &&
      node.get(CHILDKEYNAME)[ckey])
    delete node.get(CHILDKEYNAME)[ckey]

  if (!node.has(CHILDKEYSDOM))
    return [node, null]

  node = node.set(CHILDKEYSDOM, node.get(CHILDKEYSDOM)
    .filter((key, i) => (key !== ckey || (childindex = i, false))))

  return [node, childindex]
}

// move node's childdom element from idxsrc to idxdest
const gnnodeChildKeysDOMElemMv = (node, idxdest, idxsrc) => (
  gnnodeChildKeysDOMSet(node, arrElemMv(
    gnnodeChildKeysDOMGet(node), idxsrc, idxdest)))

const gnnodeChildKeysGet = node => (
  node.get(CHILDKEYS, emptylist))

const gnnodeChildKeysSet = (node, list) => (
  node.set(CHILDKEYS, list))

const gnnodeChildKeysReset = node =>
  node.set(CHILDKEYS, emptylist)

const gnnodeChildKeysElemMv = (node, idxdest, idxsrc) => (
  gnnodeChildKeysSet(node, arrElemMv(
    gnnodeChildKeysGet(node), idxsrc, idxdest)))

const gnnodeNSSendersGet = node => (
  node.get(NSSENDERS, []))

const gnnodeNSTargetsGet = node => (
  node.get(NSTARGETS, []))

const gnnodeCreateFromSpec = (spec, name = spec.name, key = name) => (
  gnnodeCreate(spec, name, gnnodeKeyFormat(key)))

const gnnodeAssign = (node, spec) => (
  imOpMerge(node, spec))

const gnnodeIs = node => (
  imOpIsMap(node) && node.get(KEY))

const gnnodeIsSame = (nodea, nodeb) => (
  imOpEquals(nodea, nodeb))

const gnnodeChildKeyRm = (node, childkey, childindex = null) => [
  node.set(CHILDKEYS, gnnodeChildKeysGet(node).filter((key, i) => (
    key !== childkey || (childindex = i, false)
  ))),
  childindex
]

const gnnodeChildKeyCreate = (pnode, childname) => (
  pathJoin(pnode.get(KEY), childname))

const gnnodeCreateFromJS = js => imOpMerge(imMap(js), {
  [CHILDKEYS]: js[CHILDKEYS] || [],
  [NSTARGETS]: js[NSTARGETS] || [],
  [NSSENDERS]: js[NSSENDERS] || []
})

const gnnodeNSSenderSet = (ndsender, edge) => (
  ndsender.set(NSSENDERS, ndsender
    .get(NSSENDERS, [])
    .filter(e => !edgeIsSame(e, edge))
    .concat([edge])))

const gnnodeNSTargetSet = (ndsender, edge) => (
  ndsender.set(NSTARGETS, ndsender
    .get(NSTARGETS, [])
    .filter(e => !edgeIsSame(e, edge))
    .concat([edge])))

const gnnodeNSTargetRmTargetKey = (node, targetkey) => (
  node.set(NSTARGETS, gnnodeNSTargetsGet(node)
    .filter(edge => edgeMetaTargetKeyGet(edge) !== targetkey)))

const gnnodeNSSenderRmSenderKey = (node, senderkey) => (
  node.set(NSSENDERS, gnnodeNSSendersGet(node)
    .filter(edge => edgeMetaSenderKeyGet(edge) !== senderkey)))

const gnnodeNSTargetGetByIdPartSender = (node, idpartsender) => (
  gnnodeNSTargetsGet(node)
    .find(edge => edgeIdGet(edge).startsWith(idpartsender)))

const gnnodeNSTargetGetBySenderProp = (node, ns, senderprop) => (
  gnnodeNSTargetGetByIdPartSender(node,
    edgeIdCreatePart([node.get(KEY), ns, senderprop])))

const gnnodeNSSenderGetByIdPartTarget = (node, idparttarget) => (
  gnnodeNSSendersGet(node)
    .find(edge => edgeIdGet(edge).endsWith(idparttarget)))

const gnnodeNSSenderGetByTargetProp = (node, ns, targetprop) => (
  gnnodeNSSenderGetByIdPartTarget(node,
    edgeIdCreatePart([node.get(KEY), ns, targetprop])))

const gnnodeEdgeResolve = (gr, edgetuple) => {
  const edgekey = edgetuple[0]
  const edgens = edgetuple[1]
  const edgeprop = edgetuple[2]

  return accNSMap[edgens].propget(gr.get(edgekey), edgeprop)
}

const gnnodeEdgeResolveComposite = (gr, edgetuple, idx, val) => {
  const resolved = gnnodeEdgeResolve(gr, edgetuple)
  const arr = resolved.slice()

  arr[idx] = val

  return arr
}

export {
  gnnodeNameIsValid,
  gnnodeKeyIsValid,

  gnnodeCreate,
  gnnodeCreateFromJS,
  gnnodeCreateFromSpec,
  gnnodeIs,
  gnnodeIsSame,
  gnnodeAssign,
  gnnodeKeyFormat,

  gnnodeChildKeysDOMRm,
  gnnodeChildKeysDOMGet,
  gnnodeChildKeysDOMSet,
  gnnodeChildKeysDOMElemMv,

  gnnodeChildKeysGet,
  gnnodeChildKeysReset,
  gnnodeChildKeysElemMv,

  gnnodeChildKeyRm,
  gnnodeChildKeyCreate,

  gnnodeEdgeResolve,
  gnnodeEdgeResolveComposite,

  gnnodeNSSendersGet,
  gnnodeNSTargetsGet,
  gnnodeNSSenderSet,
  gnnodeNSTargetSet,
  gnnodeNSTargetRmTargetKey,
  gnnodeNSTargetGetBySenderProp,
  gnnodeNSTargetGetByIdPartSender,
  gnnodeNSSenderGetByTargetProp,
  gnnodeNSSenderGetByIdPartTarget,
  gnnodeNSSenderRmSenderKey
}
